import { BrokersSocietiesWithUsers, GFAFullDetails } from "@dune/types/src";
import { Button, Combobox, HorizontalStepper } from "@dune/ui";
import { ArrowLeft } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import IfSyndicOptionsContainer from "../../../component/brokers/constructionSites/IfSyndicOptionsContainer";
import {
	ConstructionSiteDetailsFormData,
	useConstructionSiteDetailsForm,
} from "../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import {
	CreateConstructionSiteClientFormData,
	useCreateConstructionSiteForm,
} from "../../../component/brokers/constructionSites/useCreateConstructionSiteForm";
import ConstructionSiteClientSelect from "../ConstructionSiteClientSelect";
import ConstructionSiteDetailsForm from "../ConstructionSiteDetailsForm";
import { useGFAFinancingsForm } from "../../../component/gfa/useGFAFinancingsForm";
import GFAFinancingsForm from "../../gfa/GFAFinancingsForm";
import GFAShareholdersAndExperiencesForm from "../../gfa/GFAShareholdersAndExperiencesForm";
import GFAAutorisationsAndReferencesForm from "../../gfa/GFAAutorisationsAndReferencesForm";
import { useGFAShareholdersAndExperiencesForm } from "../../../component/gfa/useGFAShareholdersAndExperiencesForm";
import { useGFAAutorisationsAndReferencesForm } from "../../../component/gfa/useGFAAutorisationsAndReferencesForm";
import GFAFilesForm from "../../gfa/GFAFilesForm";
import { ProjectType, SelectedClient } from "./CommonProjectForm";

interface AsBrokerData {
	isDuneUser: boolean;
	brokersSocieties: BrokersSocietiesWithUsers[];
	brokerSocietyOptions: Parameters<typeof Combobox>[0]["options"];
	selectedBrokerSociety: BrokersSocietiesWithUsers | null;
	disabled: boolean;
	setSelectedBrokerSociety: (society: BrokersSocietiesWithUsers | null) => void;
	setSearchBroker: (search: string) => void;
}

interface ClientSelectData {
	form: ReturnType<typeof useCreateConstructionSiteForm>;
	selectedClient: SelectedClient;
	canValidate: boolean;
	actionsValue: ConstructionSiteDetailsFormData["actions"];
	excludeSyndics: boolean;
	initialClientSocietyId: number | null;
	onSubmitClient: (data: CreateConstructionSiteClientFormData) => Promise<void>;
	handleSubmitClient: ReturnType<
		typeof useCreateConstructionSiteForm
	>["handleSubmit"];
	setSelectedClient: (client: SelectedClient) => void;
}

interface ConstructionSiteDetailsData {
	form: ReturnType<typeof useConstructionSiteDetailsForm>["form"];
	clientIsSyndic: boolean;
	canSave: boolean;
	currentUserIsClient: boolean;
	handleSubmit: Parameters<
		typeof ConstructionSiteDetailsForm
	>[0]["handleSubmit"];
	onSubmit: (
		data: ConstructionSiteDetailsFormData & {
			isNext?: boolean;
			step?: number;
		},
	) => Promise<void>;
	onSave: (data: ConstructionSiteDetailsFormData) => void;
	/** When false, step 3 (offers) is hidden and the "Obtenir ma tarification" button is hidden */
	showOfferStepButton?: boolean;
	isSubmittingDetails?: boolean;
}

interface GFAData {
	GFAFullDetails: GFAFullDetails;
	financings: {
		form: ReturnType<typeof useGFAFinancingsForm>["form"];
		onSubmit: Parameters<typeof GFAFinancingsForm>[0]["onSubmit"];
	};
	shareholdersAndExperiences: {
		form: ReturnType<typeof useGFAShareholdersAndExperiencesForm>["form"];
		onSubmit: Parameters<
			typeof GFAShareholdersAndExperiencesForm
		>[0]["onSubmit"];
	};
	authorisationsAndReferences: {
		form: ReturnType<typeof useGFAAutorisationsAndReferencesForm>["form"];
		onSubmit: Parameters<
			typeof GFAAutorisationsAndReferencesForm
		>[0]["onSubmit"];
	};
}

interface StepsData {
	steps: Parameters<typeof HorizontalStepper>[0]["steps"];
	activeStepIndex: number;
	setCurrentStepIndex: (step: number) => void;
	canNavigateFromTo?: (from: number, to: number) => boolean;
}

type ProjectFormProps = {
	pageTitle: string;
	stepsData: StepsData;
	asBrokerData: AsBrokerData | null;
	isCurrentUserSyndic: boolean;
	detailsData: ConstructionSiteDetailsData;
	clientSelectData: ClientSelectData;
	onBack: () => void;
	projectType: ProjectType;
	GFAData: GFAData | undefined;
};

export const BaseProjectForm: FC<ProjectFormProps> = ({
	pageTitle,
	projectType,
	stepsData,
	asBrokerData,
	isCurrentUserSyndic,
	detailsData,
	clientSelectData,
	GFAData,
	onBack,
}) => {
	return (
		<div className="flex flex-col w-full items-center justify-center">
			<div className="w-full px-6 pt-6 pb-4 border-bottom border-solid border-1 border-gray-100">
				<Button
					className="self-start text-[#144C65] px-0 has-[>svg]:px-0"
					variant="link"
					size="sm"
					onClick={onBack}
				>
					<ArrowLeft /> Retour
				</Button>
				<h1 className="text-[#144C65] text-[32px] font-medium">{pageTitle}</h1>
			</div>

			<div className="w-full">
				<HorizontalStepper
					steps={stepsData.steps}
					currentStepIndex={stepsData.activeStepIndex}
					setCurrentStepIndex={stepsData.setCurrentStepIndex}
					canNavigateFromTo={stepsData.canNavigateFromTo}
					className="px-6"
				/>
			</div>

			{!!asBrokerData && asBrokerData.isDuneUser && (
				<div className="self-start p-6">
					<span className="text-[#144C65] text-[18px] font-medium">
						En tant que :{" "}
					</span>
					<Combobox
						triggerLabel="Sélectionner un apporteur"
						optionName="apporteur"
						options={asBrokerData.brokerSocietyOptions}
						value={asBrokerData.selectedBrokerSociety?.id.toString() ?? ""}
						onValueChange={(value) => {
							if (value === "") {
								asBrokerData.setSelectedBrokerSociety(null);
							} else {
								const selectedSociety = asBrokerData.brokersSocieties.find(
									(society) => society.id.toString() === value,
								);
								asBrokerData.setSelectedBrokerSociety(selectedSociety || null);
							}
						}}
						onSearch={asBrokerData.setSearchBroker}
						disabled={asBrokerData.disabled}
					/>
				</div>
			)}

			{stepsData.activeStepIndex === 1 && (
				<>
					{isCurrentUserSyndic && (
						<div className="mt-4">
							<FormProvider {...detailsData.form}>
								<IfSyndicOptionsContainer />
							</FormProvider>
						</div>
					)}
					<FormProvider {...clientSelectData.form}>
						<ConstructionSiteClientSelect
							handleSubmit={clientSelectData.handleSubmitClient}
							onSubmit={clientSelectData.onSubmitClient}
							selectedClient={clientSelectData.selectedClient}
							setSelectedClient={clientSelectData.setSelectedClient}
							canValidate={clientSelectData.canValidate}
							actionsValue={clientSelectData.actionsValue}
							excludeSyndics={clientSelectData.excludeSyndics}
							initialClientSocietyId={clientSelectData.initialClientSocietyId}
						/>
					</FormProvider>
				</>
			)}

			{stepsData.activeStepIndex === 2 && (
				<FormProvider {...detailsData.form}>
					<ConstructionSiteDetailsForm
						handleSubmit={detailsData.handleSubmit}
						onSubmit={detailsData.onSubmit}
						onSave={detailsData.onSave}
						clientIsSyndic={detailsData.clientIsSyndic}
						isForm={true}
						isGFA={projectType === "gfa"}
						canSave={detailsData.canSave}
						currentUserIsClient={detailsData.currentUserIsClient}
						showOfferStepButton={detailsData.showOfferStepButton}
						isSubmittingDetails={detailsData.isSubmittingDetails}
					/>
				</FormProvider>
			)}

			{projectType === "gfa" && GFAData && (
				<>
					{stepsData.activeStepIndex === 3 && (
						<FormProvider {...GFAData.financings.form}>
							<GFAFinancingsForm onSubmit={GFAData.financings.onSubmit} />
						</FormProvider>
					)}
					{stepsData.activeStepIndex === 4 && (
						<FormProvider {...GFAData.shareholdersAndExperiences.form}>
							<GFAShareholdersAndExperiencesForm
								onSubmit={GFAData.shareholdersAndExperiences.onSubmit}
							/>
						</FormProvider>
					)}
					{stepsData.activeStepIndex === 5 && (
						<FormProvider {...GFAData.authorisationsAndReferences.form}>
							<GFAAutorisationsAndReferencesForm
								onSubmit={GFAData.authorisationsAndReferences.onSubmit}
							/>
						</FormProvider>
					)}
					{stepsData.activeStepIndex === 6 && GFAData.GFAFullDetails && (
						<GFAFilesForm gfa={GFAData.GFAFullDetails} />
					)}
				</>
			)}
		</div>
	);
};
